import { Spin } from "antd";
import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AdministrationPage } from "../features/administration/AdministrationPage";
import { CemeteryDetailPage } from "../features/cemeteries/CemeteryDetailPage";
import { CemeteryPage } from "../features/cemeteries/CemeteryPage";
import { GisMapPage } from "../features/cemeteries/gis/GisMapPage";
import { GisImportPage } from "../features/cemeteries/gis/import/GisImportPage";
import { MemorialEntriesAdminPage } from "../features/cemeteries/memorial/MemorialEntriesAdminPage";
import { GraveDetailPage } from "../features/graves/GraveDetailPage";
import { MartyrDetailPage } from "../features/martyrs/MartyrDetailPage";
import { MartyrListPage } from "../features/martyrs/MartyrListPage";
import { PublicPortalHome } from "../features/public-portal/PublicPortalHome";
import { PublicPortalLayout } from "../features/public-portal/PublicPortalLayout";
import { RoleAssignmentsPage } from "../features/role-assignments/RoleAssignmentsPage";
import { UserAccountsPage } from "../features/user-accounts/UserAccountsPage";
import { AppShell } from "../layout/AppShell";
import { RouteMemory } from "../auth/RouteMemory";
import { NccAntdProvider } from "./NccAntdProvider";

/**
 * Shell công khai của SCR-PUB-3D-01 nằm trong CHUNK RIÊNG: `lazy()` giữ `three`/R3F ra khỏi
 * bundle khởi tạo, kể cả khi entrypoint tĩnh-import `App.tsx` để chọn nhánh.
 */
const PublicCemeteryTourRoute = lazy(async () => {
  const module = await import("../features/cemetery-3d/PublicCemeteryTour");
  return { default: module.PublicCemeteryTour };
});

/** Hồ sơ mộ minh họa S11.9.5 — cùng chunk lười, cùng ranh giới không-OIDC. */
const PublicDemoMartyrProfileRoute = lazy(async () => {
  const module = await import("../features/cemetery-3d/PublicDemoMartyrProfile");
  return { default: module.PublicDemoMartyrProfile };
});

/** Trang danh sách và tra cứu liệt sĩ công khai. */
const PublicMartyrSearchPageRoute = lazy(async () => {
  const module = await import("../features/public-portal/PublicMartyrSearchPage");
  return { default: module.PublicMartyrSearchPage };
});

/**
 * Route table của shell. S11.6 bổ sung sáu route lưới liệt sĩ–nghĩa trang–mộ
 * (SCR-025/026/032/033/036/037). `findNavigationItem` chọn theo tiền tố nên route con
 * (`/martyrs/:id`, `/cemeteries/:cemeteryId`, `/graves/:graveId`) vẫn tô sáng đúng mục.
 * Không import engine 3D: panel `Vị trí 3D` là seam tải lười riêng.
 */
export function App() {
  return (
    <BrowserRouter>
      <RouteMemory />
      <AppShell>
        <Routes>
          <Route path="/" element={<Navigate to="/martyrs" replace />} />
          <Route path="/martyrs" element={<MartyrListPage />} />
          <Route path="/martyrs/:id" element={<MartyrDetailPage />} />
          <Route path="/cemeteries" element={<CemeteryPage />} />
          <Route path="/cemeteries/:cemeteryId" element={<CemeteryDetailPage />} />
          <Route path="/cemeteries/:cemeteryId/gis" element={<GisMapPage />} />
          <Route path="/cemeteries/:cemeteryId/gis/import" element={<GisImportPage />} />
          <Route path="/cemeteries/memorial-entries" element={<MemorialEntriesAdminPage />} />
          <Route path="/graves/:graveId" element={<GraveDetailPage />} />
          <Route path="/administration/accounts" element={<UserAccountsPage />} />
          <Route path="/administration/roles-scopes" element={<RoleAssignmentsPage />} />
          <Route path="/administration" element={<AdministrationPage />} />
          <Route path="*" element={<Navigate to="/martyrs" replace />} />
        </Routes>
      </AppShell>
    </BrowserRouter>
  );
}

/**
 * Gốc công khai S11.8.4 + S11.11.3: `NccAntdProvider + BrowserRouter` với các route công khai nằm
 * NGOÀI `AppShell` — không sidebar, không header, không điều hướng quản trị và không actor/phiên
 * đăng nhập. Entrypoint chỉ render gốc này cho các pathname khớp mẫu ở `app/publicRoute.ts`.
 *
 * Trang cổng `/` (`PublicPortalHome`) được TĨNH-import: nó không chạm engine 3D nên không
 * kéo `three` vào bundle khởi tạo. Hai màn còn lại đi qua `lazy()` + `Suspense` nên lượt tải trang
 * quản trị không kéo `three`/`@react-three/fiber`/GLTFLoader vào bundle khởi tạo.
 */
export function PublicApp() {
  /**
   * S11.12.3 — mỗi route tự bọc lấy khung của nó, thay vì một khung chung bọc cả ba.
   *
   * Trang tham quan cần variant `immersive` (khung bỏ header + chân trang vì khung 3D chạy hết
   * viewport và nav của nó do `PublicTourChrome` dựng). Một khung chung không biết mình đang phục
   * vụ route nào, nên phương án duy nhất không phải để layout đi đọc `useLocation()` là chọn variant
   * ngay tại đây.
   *
   * `Suspense` **nằm trong** khung, không nằm ngoài: đặt ngoài thì lượt tải lười của chunk 3D sẽ
   * thay cả cây — kể cả khung — bằng spinner trần, đúng lúc trang đang cần khung nhất.
   */
  const loadingFallback = (
    <div
      className="s11-public-tour-loading"
      role="status"
      aria-label="Đang tải khung cảnh 3D"
    >
      <Spin size="small" />
    </div>
  );

  return (
    <NccAntdProvider>
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <PublicPortalLayout>
                <PublicPortalHome />
              </PublicPortalLayout>
            }
          />
          <Route path="/public" element={<Navigate to="/" replace />} />
          <Route
            path="/danh-sach-liet-si"
            element={
              <PublicPortalLayout>
                <Suspense fallback={loadingFallback}>
                  <PublicMartyrSearchPageRoute />
                </Suspense>
              </PublicPortalLayout>
            }
          />
          <Route path="/tra-cuu" element={<Navigate to="/danh-sach-liet-si" replace />} />
          <Route
            path="/public/cemeteries/:cemeterySlug/3d"
            element={
              <PublicPortalLayout variant="immersive">
                <Suspense fallback={loadingFallback}>
                  <PublicCemeteryTourRoute />
                </Suspense>
              </PublicPortalLayout>
            }
          />
          <Route
            path="/public/martyrs/:publicProfileKey"
            element={
              <PublicPortalLayout>
                <Suspense fallback={loadingFallback}>
                  <PublicDemoMartyrProfileRoute />
                </Suspense>
              </PublicPortalLayout>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </NccAntdProvider>
  );
}
